import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Primary actions (Save / Cancel, etc.) — pinned to the bottom of the modal */
  footer?: React.ReactNode;
  /**
   * Optional content rendered inside the header, directly under the title row (e.g. tabs).
   * Its bottom merges with the header's divider line.
   */
  headerExtra?: React.ReactNode;
  /** Default true */
  closeOnBackdrop?: boolean;
  /** Default true */
  showCloseButton?: boolean;
  /** Default true */
  closeOnEscape?: boolean;
}

const ModalFooterSlotContext = React.createContext<((node: React.ReactNode) => void) | null>(null);

/** Register modal footer content from anywhere inside `<Modal>` (e.g. form actions that need local state). */
export function ModalFooter({ children }: { children: React.ReactNode }) {
  const setFooter = React.useContext(ModalFooterSlotContext);
  useLayoutEffect(() => {
    if (!setFooter) return;
    setFooter(children);
    return () => setFooter(null);
  }, [setFooter, children]);
  return null;
}

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer: footerProp,
  headerExtra,
  closeOnBackdrop = true,
  showCloseButton = true,
  closeOnEscape = true,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [footerSlot, setFooterSlot] = useState<React.ReactNode>(null);
  const footerBottom = footerProp ?? footerSlot;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (!closeOnEscape) return;
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, closeOnEscape]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      const root = panelRef.current;
      if (!root) return;
      const focusable = root.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="app-modal-root" role="presentation">
      <div
        className="app-modal-backdrop"
        aria-hidden
        onClick={closeOnBackdrop ? onClose : undefined}
      />
      <div
        ref={panelRef}
        className={`app-modal-panel${footerBottom ? ' app-modal-panel--has-footer' : ''}${headerExtra ? ' app-modal-panel--has-header-extra' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-modal-header">
          <div className="app-modal-header-row">
            <h2 id={titleId} className="app-modal-title">
              {title}
            </h2>
            {showCloseButton && (
              <button
                type="button"
                className="app-modal-close"
                onClick={onClose}
                aria-label="Închide"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {headerExtra ? <div className="app-modal-header-extra">{headerExtra}</div> : null}
        </div>
        <ModalFooterSlotContext.Provider value={setFooterSlot}>
          <div className="app-modal-body">{children}</div>
        </ModalFooterSlotContext.Provider>
        {footerBottom ? <div className="app-modal-footer">{footerBottom}</div> : null}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
